import React from "react";
import { useTranslation } from "react-i18next";

export default function PreviewQuestion({
  question,
  index,
  value,
  onChange,
  themeStyles,
}) {
  const { t } = useTranslation();

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  const handleCheckboxChange = (optionId, checked) => {
    // value 타입 안전 처리
    let currentValue;
    if (Array.isArray(value)) {
      currentValue = { optionIds: value, reasons: {} };
    } else if (typeof value === "object" && value !== null) {
      currentValue = { optionIds: [], reasons: {}, ...value };
    } else {
      currentValue = { optionIds: [], reasons: {} };
    }

    const currentIds = Array.isArray(currentValue.optionIds)
      ? currentValue.optionIds
      : [];

    let newIds;
    if (checked) {
      newIds = [...currentIds, optionId];
    } else {
      newIds = currentIds.filter((id) => id !== optionId);
      // 체크 해제시 해당 reason도 제거
      const newReasons = { ...currentValue.reasons };
      delete newReasons[optionId];
      onChange({ ...currentValue, optionIds: newIds, reasons: newReasons });
      return;
    }

    onChange({ ...currentValue, optionIds: newIds });
  };

  const handleRadioChange = (optionId) => {
    // value가 문자열인 경우 (기존 형태) 처리
    let currentValue;
    if (typeof value === "string") {
      currentValue = { optionId: value, reason: "" };
    } else if (typeof value === "object" && value !== null) {
      currentValue = { optionId: "", reason: "", ...value };
    } else {
      currentValue = { optionId: "", reason: "" };
    }

    onChange({ ...currentValue, optionId: optionId.toString() });
  };

  const handleReasonChange = (optionId, reasonText, isCheckbox = false) => {
    if (isCheckbox) {
      const currentValue = value || { optionIds: [], reasons: {} };
      const newReasons = { ...currentValue.reasons };
      // 체크박스는 빈 문자열도 저장 (JSON으로 저장되므로 순서 문제 없음)
      newReasons[optionId] = reasonText;
      onChange({ ...currentValue, reasons: newReasons });
    } else {
      const currentValue = value || { optionId: "", reason: "" };
      onChange({ ...currentValue, reason: reasonText });
    }
  };

  const renderQuestionInput = () => {
    switch (question.question_type) {
      case "short_answer":
        return (
          <input
            type={question.input_type || "text"}
            className={themeStyles.answerInput}
            placeholder={t("common.survey.answerPlaceholder")}
            value={value || ""}
            onChange={handleInputChange}
            required={question.is_required}
          />
        );

      case "long_answer":
        return (
          <textarea
            className={themeStyles.answerTextarea}
            placeholder={t("common.survey.answerPlaceholder")}
            value={value || ""}
            onChange={handleInputChange}
            required={question.is_required}
            rows={4}
          />
        );

      case "multiple_choice":
        return (
          <div className={themeStyles.optionsContainer}>
            {question.options
              ?.sort((a, b) => (a.order || 0) - (b.order || 0))
              ?.map((option) => {
                // value 타입 안전 처리
                let currentValue;
                if (typeof value === "string") {
                  currentValue = { optionId: value, reason: "" };
                } else if (typeof value === "object" && value !== null) {
                  currentValue = { optionId: "", reason: "", ...value };
                } else {
                  currentValue = { optionId: "", reason: "" };
                }

                const isSelected =
                  currentValue.optionId === option.id.toString();

                return (
                  <label key={option.id} className={themeStyles.optionLabel}>
                    <input
                      type="radio"
                      name={`question_${question.id}`}
                      value={option.id}
                      checked={isSelected}
                      onChange={() => handleRadioChange(option.id)}
                      required={question.is_required}
                      className={themeStyles.radioInput}
                    />
                    <span className={themeStyles.optionText}>
                      {option.option_text}
                    </span>
                    {question.extra_reason && (
                      <input
                        type="text"
                        className={themeStyles.reasonInput}
                        placeholder={t("common.survey.reasonInputPlaceholder")}
                        value={isSelected ? currentValue.reason || "" : ""}
                        onChange={(e) =>
                          handleReasonChange(option.id, e.target.value, false)
                        }
                        disabled={!isSelected}
                      />
                    )}
                  </label>
                );
              }) || []}
          </div>
        );

      case "checkbox":
        return (
          <div className={themeStyles.optionsContainer}>
            {question.options
              ?.sort((a, b) => (a.order || 0) - (b.order || 0))
              ?.map((option) => {
                // value 타입 안전 처리
                let currentValue;
                if (Array.isArray(value)) {
                  currentValue = { optionIds: value, reasons: {} };
                } else if (typeof value === "object" && value !== null) {
                  currentValue = { optionIds: [], reasons: {}, ...value };
                } else {
                  currentValue = { optionIds: [], reasons: {} };
                }

                const isChecked =
                  Array.isArray(currentValue.optionIds) &&
                  currentValue.optionIds.includes(option.id);

                return (
                  <label key={option.id} className={themeStyles.optionLabel}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        handleCheckboxChange(option.id, e.target.checked)
                      }
                      className={themeStyles.checkboxInput}
                    />
                    <span className={themeStyles.optionText}>
                      {option.option_text}
                    </span>
                    {question.extra_reason && (
                      <input
                        type="text"
                        className={themeStyles.reasonInput}
                        placeholder={t("common.survey.reasonInputPlaceholder")}
                        value={
                          isChecked
                            ? currentValue.reasons?.[option.id] || ""
                            : ""
                        }
                        onChange={(e) =>
                          handleReasonChange(option.id, e.target.value, true)
                        }
                        disabled={!isChecked}
                      />
                    )}
                  </label>
                );
              }) || []}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={themeStyles.questionContainer}>
      <div className={themeStyles.questionHeader}>
        <h3 className={themeStyles.questionTitle}>
          {question.question_text}
          {question.is_required && (
            <span className={themeStyles.required}>*</span>
          )}
        </h3>
        {question.question_description && (
          <p className={themeStyles.questionDescription}>
            {question.question_description}
          </p>
        )}
      </div>

      <div className={themeStyles.questionContent}>{renderQuestionInput()}</div>
    </div>
  );
}
